import { ApiClientError, NetworkError } from '@recipes/api-client';
import {
  GROCERY_CATEGORIES,
  SHOPPING_LIST_SORTS,
  type AddRecipeToShoppingListRequest,
  type GroceryCategory,
  type ShoppingBulkRequest,
  type ShoppingItemWriteInput,
  type ShoppingListItem,
  type ShoppingListSort,
  type ShoppingSuggestion,
} from '@recipes/contracts';
import { decideShoppingAdd, groceryCategoryHue, reactivationNeedsQuestion } from '@recipes/domain';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ListChecks,
  MoreHorizontal,
  Pencil,
  ReceiptText,
  RefreshCw,
  ScanBarcode,
  Tag,
  Trash2,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { HueDot } from '@/features/appearance/hue';
import { sortItems, useCollapsedDepartments, useListSort } from '@/features/shopping/list-view';
import { currentFormatLocale } from '@/lib/i18n';
import { departmentOrderOf } from '@/features/catalog/department-order';
import { DepartmentOrderDialog } from '@/features/catalog/department-order-dialog';
import { useItemPictures, useShoppingAt } from '@/features/catalog/hooks';
import { ProductThumbButton, type ProductPictureRef } from '@/features/catalog/product-photos';
import { ItemHintDetails, boughtElsewhere, useListHints } from '@/features/catalog/list-hints';
import { PaybackButton } from '@/features/catalog/loyalty-cards';
import { ShoppingAtButton, ShoppingAtLine } from '@/features/catalog/shopping-at';
import {
  formatMoney,
  useItemPrices,
  useListEstimates,
  usePricesEnabled,
  type ItemPriceInfo,
} from '@/features/prices/hooks';
import { ListEstimateFooter } from '@/features/prices/list-estimate';
import { PriceDialog, type PriceTarget } from '@/features/prices/price-dialog';
import { PriceChangeNote } from '@/features/prices/price-parts';
import { AddField } from '@/features/shopping/add-field';
import { AddRecipeDialog } from '@/features/shopping/add-recipe-dialog';
import { storeListDetail } from '@/features/shopping/device-copy';
import {
  useLocalList,
  usePendingShoppingCount,
  useShoppingHistory,
  useShoppingLive,
  useShoppingPreferences,
  useShoppingSyncState,
} from '@/features/shopping/hooks';
import { ItemFormDialog } from '@/features/shopping/item-form-dialog';
import { ScanSheet } from '@/features/shopping/scan-sheet';
import {
  BoughtSection,
  ListStatus,
  ReactivationSheet,
  sortBought,
  useBoughtOpen,
  type ReactivationQuestion,
} from '@/features/shopping/list-parts';
import { ReminderDialog } from '@/features/shopping/reminder-dialog';
import { describeReminder } from '@/features/shopping/reminder-format';
import { ShareDialog } from '@/features/shopping/share-dialog';
import {
  addItemLocal,
  clearBoughtLocal,
  kickShoppingReplay,
  removeItemLocal,
  setCheckedLocal,
  updateItemLocal,
  type LocalAddResult,
} from '@/features/shopping/shopping-queue';
import { VoiceButton, VoicePanel, useShoppingVoice } from '@/features/shopping/voice-input';
import { groceryCategoryLabel } from '@/features/shared/labels';
import { useFeature } from '@/features/shared/hooks';
import { PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

/**
 * The open items by department, in the order the store is walked through (the default order
 * without a store). Inside a department the items keep the order they were added in.
 */
export function groupItems(
  items: ShoppingListItem[],
  order: readonly GroceryCategory[] = GROCERY_CATEGORIES,
) {
  const groups = new Map<string, ShoppingListItem[]>();
  for (const item of [...items].sort((a, b) => a.position - b.position)) {
    // A category this client does not know (newer server) must not hide the item.
    const category = (GROCERY_CATEGORIES as readonly string[]).includes(item.groceryCategory)
      ? item.groceryCategory
      : 'other';
    const list = groups.get(category) ?? [];
    list.push(item);
    groups.set(category, list);
  }
  // A department the order does not name (it should name all) still shows, at the end.
  const complete = [...order, ...GROCERY_CATEGORIES.filter((c) => !order.includes(c))];
  return complete
    .filter((c) => groups.has(c))
    .map((c) => ({
      category: c,
      items: groups.get(c) as ShoppingListItem[],
    }));
}

/**
 * One shopping list. The page renders the device copy (IndexedDB): every change is written
 * there first and sent afterwards, the live stream and the polling fallback write there too –
 * so the list looks and works the same with and without a connection.
 */
export function ShoppingListPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const [editItem, setEditItem] = useState<ShoppingListItem | null>(null);
  // "Remind me": for the list itself, or for one item.
  const [reminderFor, setReminderFor] = useState<'list' | ShoppingListItem | null>(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemSaving, setItemSaving] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // `?scan=<barcode>` opens the sheet with that code (deep links, tests without a camera).
  const [searchParams] = useSearchParams();
  const scanParam = searchParams.get('scan');
  const [scanOpen, setScanOpen] = useState(scanParam !== null);
  // "Scannen und zuordnen" from an item's menu: the scanned product goes to this item.
  const [scanTarget, setScanTarget] = useState<ShoppingListItem | null>(null);
  const [priceFor, setPriceFor] = useState<PriceTarget | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [question, setQuestion] = useState<ReactivationQuestion | null>(null);
  const answer = useRef<((choice: 'NEW' | 'KEEP' | null) => void) | null>(null);
  const [boughtOpen, setBoughtOpen] = useBoughtOpen();

  const { list, items, pendingIds } = useLocalList(id);
  const preferences = useShoppingPreferences();
  const history = useShoppingHistory();
  const syncState = useShoppingSyncState();
  const pendingCount = usePendingShoppingCount();
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const invalidateLists = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: queryKeys.shopping.all, exact: true }),
    [queryClient],
  );
  const onGone = useCallback(() => {
    toast.show({ title: t('shopping.live.gone') });
    invalidateLists();
    navigate('/shopping', { replace: true });
  }, [invalidateLists, navigate, t]);

  // Only a list that is on this device is kept live; an unknown id is handled further down.
  const live = useShoppingLive(list ? id : '', {
    onGone,
    describe: (event) =>
      event.item.checked
        ? t('shopping.live.checked', { name: event.item.name })
        : t('shopping.live.changed', { name: event.item.name }),
  });

  // Ingredient catalog: hints per item, filtered by the store the user is shopping in.
  const catalogEnabled = useFeature('ingredient_catalog');
  const { store, stores } = useShoppingAt();
  const pricesEnabled = usePricesEnabled();
  const receiptsEnabled = useFeature('receipt_scan') && pricesEnabled;
  const estimates = useListEstimates(items, store, stores);
  const itemPrices = useItemPrices(pricesEnabled ? estimates.here : null);
  const [hideElsewhere, setHideElsewhere] = useState(false);
  const itemHints = useListHints(catalogEnabled ? id : '', items, store);
  const openItems = useMemo(
    () =>
      items.filter(
        (item) =>
          !item.checked && !(hideElsewhere && store && boughtElsewhere(itemHints.get(item.id))),
      ),
    [items, hideElsewhere, store, itemHints],
  );
  const bought = useMemo(() => sortBought(items.filter((i) => i.checked)), [items]);
  const hiddenCount = items.length - bought.length - openItems.length;
  const departmentOrder = useMemo(() => departmentOrderOf(store, stores), [store, stores]);
  const groups = useMemo(
    () => groupItems(openItems, departmentOrder),
    [openItems, departmentOrder],
  );
  const [orderOpen, setOrderOpen] = useState(false);
  const [sort, setSort] = useListSort();
  const departments = useCollapsedDepartments(id);
  const flatItems = useMemo(
    () => (sort === 'DEPARTMENT' ? [] : sortItems(openItems, sort, currentFormatLocale())),
    [openItems, sort],
  );
  const allCollapsed =
    groups.length > 0 && groups.every((g) => departments.collapsed.has(g.category));
  const pictures = useItemPictures(catalogEnabled ? items : []);

  // What was just added or changed must be seen – also inside a folded department. (The item
  // may reach the device copy a moment after it was highlighted.)
  const { expand: expandDepartment } = departments;
  useEffect(() => {
    const changed = flash ? items.find((item) => item.id === flash) : undefined;
    if (changed && !changed.checked) expandDepartment(changed.groceryCategory);
  }, [flash, items, expandDepartment]);

  const highlight = (itemId: string) => {
    setFlash(itemId);
    setTimeout(() => setFlash((current) => (current === itemId ? null : current)), 2500);
  };

  const undo = async (result: LocalAddResult) => {
    const previous = result.previous;
    if (!previous) return;
    try {
      await updateItemLocal(result.item, {
        name: previous.name,
        quantity: previous.quantity,
        quantityMax: previous.quantityMax,
        quantityText: previous.quantityText,
        unitCode: previous.unitCode,
        unitText: previous.unitText,
        note: previous.note,
        groceryCategory: previous.groceryCategory,
      });
      if (previous.checked) await setCheckedLocal(result.item, true);
    } catch (error) {
      toastError(error);
    }
  };

  const report = (result: LocalAddResult) => {
    const { item } = result;
    const amount = item.display ? `${item.display} ` : '';
    if (result.outcome === 'UNCHANGED') {
      toast.show({ title: t('shopping.add.unchanged', { name: item.name }) });
      highlight(item.id);
    } else if (result.outcome === 'MERGED') {
      toast.show({ title: t('shopping.add.merged', { name: item.name, amount: item.display }) });
      highlight(item.id);
    } else if (result.outcome === 'REACTIVATED') {
      toast.show({
        title: t('shopping.add.reactivated', { name: `${amount}${item.name}` }),
        durationMs: 8000,
        action: { label: t('common.undo'), onAction: () => void undo(result) },
      });
      highlight(item.id);
    }
  };

  /** Asks only where a person is looking at the screen (typed, suggestion, voice chips). */
  const askIfNeeded = (input: ShoppingItemWriteInput): Promise<'NEW' | 'KEEP' | null> => {
    if (preferences.reactivation !== 'ASK') return Promise.resolve('NEW');
    const incoming = {
      name: input.name,
      quantity: input.quantity ?? null,
      quantityMax: input.quantityMax ?? null,
      quantityText: input.quantityText ?? null,
      unitCode: input.unitCode ?? null,
      unitText: input.unitText ?? null,
      refs: [],
    };
    const decision = decideShoppingAdd(
      itemsRef.current.map((item) => ({ ...item, refs: item.recipeRefs })),
      incoming,
    );
    const target = itemsRef.current.find((item) => item.id === decision.targetId);
    if (decision.outcome !== 'REACTIVATED' || !target) return Promise.resolve('NEW');
    if (!reactivationNeedsQuestion(target, incoming)) return Promise.resolve('NEW');
    return new Promise((resolve) => {
      answer.current = resolve;
      setQuestion({ bought: target, input });
    });
  };

  const [adding, setAdding] = useState(false);
  const add = async (inputs: ShoppingItemWriteInput[], ask: boolean): Promise<string[]> => {
    const names: string[] = [];
    setAdding(true);
    try {
      for (const input of inputs) {
        const choice = ask ? await askIfNeeded(input) : 'NEW';
        if (choice === null) continue;
        // Local and immediate; the field must never wait for the network.
        const result = await addItemLocal(id, input, {
          reactivateQuantity: choice,
          replay: false,
        });
        report(result);
        names.push(result.item.name);
      }
      kickShoppingReplay(invalidateLists);
    } catch (error) {
      toastError(error);
    } finally {
      setAdding(false);
    }
    return names;
  };

  const voice = useShoppingVoice({
    knownNames: useMemo(
      () => [...history.map((h) => h.displayName), ...items.map((i) => i.name)],
      [history, items],
    ),
    onAdd: (inputs, handsFree) => add(inputs, !handsFree),
    // "ich habe Milch" / "Milch erledigt" (user 2026-09-30): the open items are what can be checked.
    openItems: items.filter((i) => !i.checked),
    onCheck: async (targets) => {
      const names: string[] = [];
      for (const target of targets) {
        const item = items.find((i) => i.id === target.id);
        if (!item || item.checked) continue;
        await setCheckedLocal(item, true);
        names.push(item.name);
      }
      invalidateLists();
      return names;
    },
  });

  const hide = async (suggestion: ShoppingSuggestion) => {
    if (!suggestion.historyId) return;
    const row = await offlineDb.shoppingHistory.get(suggestion.historyId);
    if (row) await offlineDb.shoppingHistory.put({ ...row, entry: { ...row.entry, hidden: true } });
    try {
      await api.shopping.hideSuggestion(suggestion.historyId);
      void queryClient.invalidateQueries({ queryKey: ['shopping', 'suggestions', id] });
    } catch (error) {
      if (!(error instanceof NetworkError)) toastError(error);
    }
  };

  const saveItem = async (body: ShoppingItemWriteInput) => {
    if (!editItem) {
      setItemDialogOpen(false);
      await add([body], true);
      return;
    }
    setItemSaving(true);
    try {
      // Online the server checks that the edit is based on the current item (409 otherwise);
      // offline the change is queued and rebased at replay.
      const busy = pendingIds.has(editItem.id);
      if (online && !busy) {
        const updated = await api.shopping.updateItem(id, editItem.id, {
          ...body,
          baseUpdatedAt: editItem.updatedAt,
        });
        await offlineDb.shoppingItems.put({ id: updated.id, listId: id, item: updated });
      } else {
        await updateItemLocal(editItem, body);
      }
      setItemDialogOpen(false);
      setEditItem(null);
    } catch (error) {
      if (error instanceof NetworkError) {
        await updateItemLocal(editItem, body);
        setItemDialogOpen(false);
        setEditItem(null);
      } else if (error instanceof ApiClientError && error.status === 409) {
        // Someone changed the item meanwhile: show theirs, keep the user's text in the form.
        const current = (error.error.details as { current?: ShoppingListItem } | undefined)
          ?.current;
        if (current) {
          await offlineDb.shoppingItems.put({ id: current.id, listId: id, item: current });
          setEditItem((prev) => (prev ? { ...prev, updatedAt: current.updatedAt } : prev));
        }
        toast.error(t('shopping.item.conflict'));
      } else toastError(error);
    } finally {
      setItemSaving(false);
    }
  };

  const bulk = useMutation({
    mutationFn: async (action: ShoppingBulkRequest['action']) => {
      if (action === 'REMOVE_CHECKED') return clearBoughtLocal(id);
      await storeListDetail(await api.shopping.bulk(id, { action }));
    },
    onSuccess: invalidateLists,
    onError: (e) => toastError(e),
  });
  const addRecipe = useMutation({
    mutationFn: (body: AddRecipeToShoppingListRequest) => api.shopping.addRecipe(id, body),
    onSuccess: async (detail) => {
      await storeListDetail(detail);
      setRecipeOpen(false);
      toast.success(t('shopping.addRecipe.done'));
      if (detail.skippedStaples?.length)
        toast.show({
          title: t('shopping.addRecipe.skippedStaples', {
            items: detail.skippedStaples.join(', '),
          }),
        });
      invalidateLists();
    },
    onError: (e) => toastError(e),
  });

  // The list is not on this device and cannot be fetched: gone, or never synchronised.
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    if (list !== null) {
      setMissing(false);
      return;
    }
    if (!online) {
      setMissing(true);
      return;
    }
    let cancelled = false;
    api.shopping
      .get(id)
      .then((detail) => storeListDetail(detail))
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [list, online, id]);

  if (!list)
    return (
      <PageContainer>
        {missing ? (
          <Alert variant="destructive">
            {online ? t('shopping.notFound') : t('shopping.offline.notOnDevice')}
          </Alert>
        ) : (
          <Spinner label={t('app.loading')} />
        )}
      </PageContainer>
    );

  const toggle = (item: ShoppingListItem, checked: boolean) =>
    void setCheckedLocal(item, checked)
      .then(invalidateLists)
      .catch((e) => toastError(e, t('shopping.checkFailed')));
  const row = (item: ShoppingListItem) => (
    <ShoppingItemRow
      key={item.id}
      item={item}
      pending={pendingIds.has(item.id)}
      highlighted={flash === item.id || live.highlighted.has(item.id)}
      onToggle={(checked) => toggle(item, checked)}
      onEdit={() => {
        setEditItem(item);
        setItemDialogOpen(true);
      }}
      onRemove={() => void removeItemLocal(item).then(invalidateLists).catch(toastError)}
      onReminder={online ? () => setReminderFor(item) : undefined}
      onScan={
        catalogEnabled
          ? () => {
              setScanTarget(item);
              setScanOpen(true);
            }
          : undefined
      }
      picture={pictures.get(item.id) ?? null}
      price={pricesEnabled ? (itemPrices.get(item.id) ?? null) : null}
      currency={estimates.currency}
      onPrice={
        pricesEnabled && (item.product?.id || item.ingredientId)
          ? () =>
              setPriceFor({
                productId: item.product?.id ?? null,
                ingredientId: item.ingredientId ?? null,
                name: item.product?.name ?? item.name,
              })
          : undefined
      }
      details={
        catalogEnabled ? (
          <ItemHintDetails item={item} hints={itemHints.get(item.id)} store={store} />
        ) : null
      }
    />
  );

  return (
    // Room below the last rows (a quarter of the screen), so they can be scrolled up to the thumb;
    // with the scan panel open on a phone, enough to scroll the whole list above the panel.
    <PageContainer className={cn('max-w-3xl md:pb-[25vh]', scanOpen ? 'pb-[66dvh]' : 'pb-[25vh]')}>
      {/* Title and back stay at the top; everything else lives in the list's menu (user, 2026-09-24:
          the header was too much). On a phone the shell's mode/language icons sit top right. */}
      <header
        className="sticky top-0 z-20 -mx-4 -mt-4 mb-3 border-b bg-background/95 px-4 py-2 backdrop-blur md:-mx-6 md:-mt-6 md:px-6"
        data-testid="shopping-header"
      >
        <div className="flex items-center gap-2 pr-[5.5rem] md:pr-0">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('common.back')}
          >
            <Link to="/shopping">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold leading-tight">{list.name}</h1>
            <p className="truncate text-xs text-muted-foreground">
              {t('shopping.progress', { checked: bought.length, total: items.length })}
              {list.householdId && ` · ${t('shared.household.shared')}`}
              {list.reminder && (
                <span data-testid="shopping-reminder-line">
                  {' '}
                  · {describeReminder(list.reminder, t)}
                </span>
              )}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={t('shopping.menu')}
                data-testid="shopping-menu"
              >
                <MoreHorizontal aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {catalogEnabled && (
                <DropdownMenuItem onSelect={() => setScanOpen(true)} data-testid="shopping-scan">
                  <ScanBarcode aria-hidden /> {t('catalog.scan.action')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={!online}
                onSelect={() => setRecipeOpen(true)}
                data-testid="shopping-add-recipe"
              >
                <BookOpen aria-hidden /> {t('shopping.addRecipe.title')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!online}
                onSelect={() => setReminderFor('list')}
                data-testid="shopping-reminder"
              >
                <Bell aria-hidden className={list.reminder ? 'text-primary' : undefined} />{' '}
                {t('shopping.reminder.action')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShareOpen(true)} data-testid="shopping-share">
                <Users aria-hidden /> {t('shopping.share.action')}
                {list.householdId && <Badge variant="muted">{t('shared.household.shared')}</Badge>}
              </DropdownMenuItem>
              {receiptsEnabled && (
                <DropdownMenuItem asChild>
                  <Link to={`/shopping/receipts/new?listId=${id}`} data-testid="shopping-receipt">
                    <ReceiptText aria-hidden /> {t('receipts.scan')}
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t('shopping.bulk.menu')}</DropdownMenuLabel>
              <DropdownMenuItem disabled={!online} onSelect={() => bulk.mutate('CHECK_ALL')}>
                <ListChecks aria-hidden /> {t('shopping.bulk.CHECK_ALL')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!online} onSelect={() => bulk.mutate('UNCHECK_ALL')}>
                {t('shopping.bulk.UNCHECK_ALL')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={bought.length === 0}
                onSelect={() => bulk.mutate('REMOVE_CHECKED')}
              >
                {t('shopping.bulk.REMOVE_CHECKED')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!online} onSelect={() => bulk.mutate('RECALCULATE')}>
                {t('shopping.bulk.RECALCULATE')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <ListStatus
        mode={live.mode}
        presence={live.presence}
        pendingCount={pendingCount}
        syncedAt={syncState?.syncedAt ?? null}
        announcement={live.announcement}
      />

      <AddField
        listId={id}
        items={items}
        busy={adding}
        onAdd={(input) => void add([input], true)}
        onHide={(suggestion) => void hide(suggestion)}
        onDetailed={() => {
          setEditItem(null);
          setItemDialogOpen(true);
        }}
        trailing={<VoiceButton voice={voice} className="hidden md:inline-flex" />}
      />
      <VoicePanel voice={voice} />

      {items.length === 0 && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t('shopping.noItems')}
        </p>
      )}
      {items.length > 0 && openItems.length === 0 && hiddenCount === 0 && (
        <p className="mb-4 rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          {t('shopping.bought.allDone')}
        </p>
      )}

      {/* One row (user, 2026-09-25): sort, the store crosshair, fold all – the store as a line below. */}
      {(openItems.length > 0 || catalogEnabled) && (
        <div className="mb-3">
          <div className="flex items-center gap-2" data-testid="shopping-view-bar">
            <Label
              htmlFor="shopping-sort"
              className="hidden text-sm text-muted-foreground sm:inline"
            >
              {t('shopping.sort.label')}
            </Label>
            <Select value={sort} onValueChange={(value) => setSort(value as ShoppingListSort)}>
              <SelectTrigger
                id="shopping-sort"
                className="h-9 w-auto min-w-0 max-w-[60%] sm:min-w-44"
                aria-label={t('shopping.sort.label')}
                data-testid="shopping-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOPPING_LIST_SORTS.map((value) => (
                  <SelectItem key={value} value={value} data-testid={`shopping-sort-${value}`}>
                    {t(`shopping.sort.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {catalogEnabled && (
              <>
                <ShoppingAtButton
                  hideElsewhere={hideElsewhere}
                  onHideElsewhere={setHideElsewhere}
                  hiddenCount={hiddenCount}
                  onDepartmentOrder={store?.editable ? () => setOrderOpen(true) : undefined}
                />
                <PaybackButton />
              </>
            )}
            {sort === 'DEPARTMENT' && groups.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                aria-label={
                  allCollapsed ? t('shopping.sort.expandAll') : t('shopping.sort.collapseAll')
                }
                onClick={() =>
                  allCollapsed
                    ? departments.expandAll()
                    : departments.collapseAll(groups.map((g) => g.category))
                }
                data-testid="shopping-collapse-all"
              >
                {allCollapsed ? <ChevronsUpDown aria-hidden /> : <ChevronsDownUp aria-hidden />}
                <span className="hidden sm:inline">
                  {allCollapsed ? t('shopping.sort.expandAll') : t('shopping.sort.collapseAll')}
                </span>
              </Button>
            )}
          </div>
          {catalogEnabled && <ShoppingAtLine className="mt-1" />}
        </div>
      )}

      {sort !== 'DEPARTMENT' && flatItems.length > 0 && (
        <ul className="mb-4 divide-y rounded-lg border" data-testid="shopping-flat-list">
          {flatItems.map(row)}
        </ul>
      )}

      {sort === 'DEPARTMENT' &&
        groups.map((group) => {
          const folded = departments.collapsed.has(group.category);
          return (
            <section
              key={group.category}
              aria-labelledby={`cat-${group.category}`}
              className="mb-4"
            >
              {/* Stays in view while its items scroll by: which department am I in? */}
              <h2
                id={`cat-${group.category}`}
                className="sticky top-0 z-10 -mx-1 mb-1 bg-background/95 backdrop-blur"
                data-testid="shopping-department"
                data-category={group.category}
                data-collapsed={folded ? 'true' : 'false'}
              >
                <button
                  type="button"
                  className="flex min-h-[40px] w-full items-center gap-2 rounded-md px-1 py-1 text-left text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-expanded={!folded}
                  aria-controls={`cat-items-${group.category}`}
                  onClick={() => departments.toggle(group.category)}
                  data-testid="shopping-department-toggle"
                >
                  <ChevronDown
                    className={cn('size-4 shrink-0 transition-transform', folded && '-rotate-90')}
                    aria-hidden
                  />
                  <HueDot hue={groceryCategoryHue(group.category)} />
                  {groceryCategoryLabel(t, group.category)}
                  <span className="text-xs font-normal normal-case tracking-normal">
                    {t('shopping.department.count', { count: group.items.length })}
                  </span>
                </button>
              </h2>
              <ul
                id={`cat-items-${group.category}`}
                className="divide-y rounded-lg border"
                hidden={folded}
              >
                {/* Folded rows are not rendered at all: nothing to tab into, nothing to measure. */}
                {!folded && group.items.map(row)}
              </ul>
            </section>
          );
        })}

      <BoughtSection
        count={bought.length}
        open={boughtOpen}
        onOpenChange={setBoughtOpen}
        onClear={() => bulk.mutate('REMOVE_CHECKED')}
      >
        {bought.map(row)}
      </BoughtSection>

      {catalogEnabled && <ListEstimateFooter items={items} store={store} />}
      {store && (
        <DepartmentOrderDialog
          store={store}
          stores={stores}
          open={orderOpen}
          onOpenChange={setOrderOpen}
          inUse={new Set(groups.map((g) => g.category))}
        />
      )}

      {/* On a phone the microphone is the lowest of the corner buttons (user 2026-10-01). */}
      <VoiceButton
        voice={voice}
        fab
        bottomClass="bottom-[calc(4.5rem+env(safe-area-inset-bottom))]"
      />
      {catalogEnabled && !scanOpen && (
        // On a phone the scanner is one tap away, bottom right above the tab bar (user, 2026-09-24).
        <Button
          size="icon"
          className={cn(
            'fixed right-4 z-20 size-14 rounded-full shadow-lg md:hidden',
            // Above the microphone when there is one.
            voice.enabled && voice.available
              ? 'bottom-[calc(8.5rem+env(safe-area-inset-bottom))]'
              : 'bottom-[calc(4.5rem+env(safe-area-inset-bottom))]',
          )}
          aria-label={t('catalog.scan.action')}
          onClick={() => setScanOpen(true)}
          data-testid="shopping-scan-fab"
        >
          <ScanBarcode aria-hidden className="size-6" />
        </Button>
      )}
      {catalogEnabled && (
        <ScanSheet
          open={scanOpen}
          onOpenChange={(next) => {
            setScanOpen(next);
            if (!next) setScanTarget(null);
          }}
          listId={id}
          items={items}
          initialBarcode={scanParam}
          assignTo={scanTarget}
        />
      )}
      {priceFor && (
        <PriceDialog
          open
          onOpenChange={(next) => !next && setPriceFor(null)}
          target={priceFor}
          defaultStoreId={store?.id ?? null}
        />
      )}
      {reminderFor && (
        <ReminderDialog
          open
          onOpenChange={(next) => !next && setReminderFor(null)}
          list={list}
          item={reminderFor === 'list' ? null : reminderFor}
          onChanged={(reminder) => {
            // The device copy shows it at once; the next sync confirms it.
            void (async () => {
              if (reminderFor === 'list') {
                await offlineDb.shoppingLists.put({ id: list.id, list: { ...list, reminder } });
              } else {
                const row = await offlineDb.shoppingItems.get(reminderFor.id);
                if (row)
                  await offlineDb.shoppingItems.put({ ...row, item: { ...row.item, reminder } });
              }
              invalidateLists();
            })();
          }}
        />
      )}
      <ItemFormDialog
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        item={editItem}
        saving={itemSaving || adding}
        onSubmit={(body) => void saveItem(body)}
      />
      <AddRecipeDialog
        open={recipeOpen}
        onOpenChange={setRecipeOpen}
        saving={addRecipe.isPending}
        onSubmit={(body) => addRecipe.mutate(body)}
      />
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        list={list}
        onChanged={(updated) => {
          void offlineDb.shoppingLists.put({ id: updated.id, list: updated });
          invalidateLists();
          toast.success(
            updated.householdId ? t('shopping.share.shared') : t('shopping.share.unshared'),
          );
        }}
      />
      <ReactivationSheet
        question={question}
        onAnswer={(choice) => {
          setQuestion(null);
          answer.current?.(choice);
          answer.current = null;
        }}
      />
    </PageContainer>
  );
}

export function ShoppingItemRow({
  item,
  onToggle,
  onEdit,
  onRemove,
  onReminder,
  onScan,
  details,
  pending,
  highlighted,
  picture,
  price,
  currency,
  onPrice,
}: {
  item: ShoppingListItem;
  onToggle: (checked: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
  /** "Remind me" for this item; absent without a connection. */
  onReminder?: () => void;
  /** Scan a product and make it this item's product; absent without the catalog. */
  onScan?: () => void;
  /** Catalog badges and hint buttons on the line, expanded hints below it (flex-wrap items). */
  details?: ReactNode;
  /** Changed on this device, not confirmed by the server yet. */
  pending?: boolean;
  /** Just changed (by someone else, or by an add that merged into it). */
  highlighted?: boolean;
  /** The picture of the product meant (chosen, or bought last time): which label was it? */
  picture?: ProductPictureRef | null;
  /** What the open item will roughly cost at the chosen store (spec 5.5). */
  price?: ItemPriceInfo | null;
  currency?: string;
  /** Record a price for the item's product or ingredient; absent when it has neither. */
  onPrice?: () => void;
}) {
  const { t } = useTranslation();
  const refs = item.recipeRefs;
  // `display` is the formatted amount only ("2 kg"); the name is rendered with it.
  const main = item.quantity
    ? `${item.display} ${item.name}`
    : item.quantityText
      ? `${item.name} (${item.quantityText})`
      : item.name;
  // Everything else stays on the same line, small and grey; the row is never taller than the
  // checkbox slot (user: the tall tiles were hard to scan). The title carries the full text.
  const secondary: string[] = [];
  if (item.note) secondary.push(item.note);
  if (item.checked && item.checkedByName)
    secondary.push(t('shopping.item.checkedBy', { name: item.checkedByName }));
  if (refs.length > 0)
    secondary.push(
      `${t('shopping.item.forRecipes')}: ${refs
        .map((r) => `${r.recipeTitle} (${r.servings} ${t('common.servings')})`)
        .join(', ')}`,
    );
  if (item.product) {
    const product = [item.product.brand, item.product.name].filter(Boolean).join(' ');
    if (product) secondary.push(product);
  }
  return (
    <li
      className={cn(
        'transition-colors duration-700',
        item.checked && 'bg-muted/40',
        highlighted && 'bg-primary/10',
      )}
      data-testid="shopping-item"
      data-checked={item.checked ? 'true' : 'false'}
      data-highlighted={highlighted ? 'true' : undefined}
    >
      {/* One line per item; the expanded hints (`details`) wrap onto a full-width line below. */}
      <div className="flex flex-wrap items-center gap-x-1 px-2">
        <label className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-2">
          <span className="flex size-11 shrink-0 items-center justify-center">
            <Checkbox
              size="lg"
              checked={item.checked}
              onCheckedChange={(c) => onToggle(c === true)}
              aria-label={t('shopping.item.check', { name: item.name })}
            />
          </span>
          <span
            className="min-w-0 flex-1 truncate text-sm"
            title={secondary.length > 0 ? [main, ...secondary].join(' · ') : undefined}
          >
            <span className={cn(item.checked && 'text-muted-foreground line-through')}>{main}</span>
            {item.checked && <span className="sr-only"> ({t('shopping.item.checked')})</span>}
            {secondary.map((text, i) => (
              <span key={i} className="text-xs text-muted-foreground">
                {' · '}
                {text}
              </span>
            ))}
          </span>
        </label>
        {picture && <ProductThumbButton product={picture} className="size-9" />}
        {/* Secondary information: small, right-aligned, no red/green judgement. */}
        {!item.checked && price && currency && (
          <span
            className="flex shrink-0 flex-col items-end text-xs tabular-nums text-muted-foreground"
            data-testid="shopping-item-price"
          >
            <span>
              {price.stale && `${t('prices.approx')} `}
              {formatMoney(price.amount, currency)}
            </span>
            {price.change && <PriceChangeNote change={price.change} compact />}
          </span>
        )}
        {item.checked && item.price && (
          <span
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
            data-testid="shopping-item-paid"
          >
            {(item.purchasedCount ?? 1) > 1 && `${item.purchasedCount} × `}
            {formatMoney(item.price, item.priceCurrency ?? currency ?? 'EUR')}
          </span>
        )}
        {item.checked && !item.price && (item.purchasedCount ?? 1) > 1 && (
          <span
            className="shrink-0 text-xs text-muted-foreground"
            data-testid="shopping-item-count"
          >
            {t('shopping.item.purchasedCount', { count: item.purchasedCount })}
          </span>
        )}
        {item.reminder && (
          <span
            className="inline-flex items-center text-muted-foreground"
            title={describeReminder(item.reminder, t)}
            data-testid="shopping-item-reminder"
          >
            <Bell aria-hidden className="size-3.5" />
            <span className="sr-only">{describeReminder(item.reminder, t)}</span>
          </span>
        )}
        {pending && (
          <span
            className="text-muted-foreground"
            title={t('shopping.item.pending')}
            data-testid="shopping-item-pending"
          >
            <RefreshCw aria-hidden className="size-3.5" />
            <span className="sr-only">{t('shopping.item.pending')}</span>
          </span>
        )}
        {details}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('shopping.item.actions', { name: item.name })}
            >
              <MoreHorizontal aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil aria-hidden /> {t('common.edit')}
            </DropdownMenuItem>
            {onReminder && (
              <DropdownMenuItem onSelect={onReminder} data-testid="shopping-item-reminder-action">
                <Bell aria-hidden /> {t('shopping.reminder.action')}
              </DropdownMenuItem>
            )}
            {onScan && (
              <DropdownMenuItem onSelect={onScan} data-testid="shopping-item-scan-action">
                <ScanBarcode aria-hidden /> {t('shopping.item.scan')}
              </DropdownMenuItem>
            )}
            {onPrice && (
              <DropdownMenuItem onSelect={onPrice} data-testid="shopping-item-price-action">
                <Tag aria-hidden /> {t('prices.action')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem destructive onSelect={onRemove}>
              <Trash2 aria-hidden /> {t('common.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
